import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Paperclip,
  MoreVertical,
  Phone,
  X,
  Check,
  CheckCheck,
  Zap,
  UserPlus,
  XCircle,
  RotateCcw,
  FileText,
  Image,
  Film,
  Music,
  Download,
  Loader2,
  Mic,
  Square,
  Trash2,
  ArrowLeft
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { cn, formatTime, getInitials, getStatusLabel, getStatusColor } from '../lib/utils';
import { Badge } from './ui/badge';
import { uploadFile, searchContactByPhone, createContact } from '../lib/api';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const ChatWindow = ({
  conversation,
  messages,
  onSendMessage,
  quickReplies,
  onTransfer,
  onClose,
  onReopen,
  onDelete,
  deleteEnabled = true,
  onAssign,
  onUnassign,
  onOpenContact,
  attendants,
  currentUser,
  chatDisabled = false,
  chatDisabledReason = '',
  isMobile = false,
  onBack = () => { }
}) => {
  useEffect(() => {
    console.log('ChatWindow selection changed:', conversation?.id);
  }, [conversation?.id]);

  const [message, setMessage] = useState('');
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showSaveContactDialog, setShowSaveContactDialog] = useState(false);
  const [contactExists, setContactExists] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    phone: '',
    email: '',
    plan: '',
    contract: ''
  });
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const textareaRef = useRef(null);
  const isAssignedToMe = !!conversation?.assigned_to && conversation?.assigned_to === currentUser?.id;
  const isClosing = conversation?.status === 'closing';
  const isClosed = conversation?.status === 'closed' || isClosing;
  const isChatAllowed = !chatDisabled && isAssignedToMe && !isClosed;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      if (message) {
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [message]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Check if contact exists when conversation changes
  useEffect(() => {
    const checkContact = async () => {
      if (!conversation?.customer_phone) return;
      setContactLoading(true);
      try {
        const phone = String(conversation.customer_phone || '').replace(/\D/g, '');
        const res = await searchContactByPhone(phone);
        setContactExists(res.data.found);
      } catch (error) {
        console.error('Error checking contact:', error);
      } finally {
        setContactLoading(false);
      }
    };
    checkContact();
  }, [conversation?.customer_phone]);

  const handleOpenSaveContact = () => {
    if (!conversation?.customer_phone) {
      toast.error('Número do cliente não encontrado');
      return;
    }
    setContactFormData({
      name: conversation.customer_name || '',
      phone: String(conversation.customer_phone || '').replace(/\D/g, ''),
      email: conversation.customer_email || '',
      plan: conversation.customer_plan || '',
      contract: conversation.customer_contract || ''
    });
    setShowSaveContactDialog(true);
  };

  const handleSaveContact = async () => {
    try {
      await createContact(contactFormData);
      setContactExists(true);
      setShowSaveContactDialog(false);
      toast.success('Contato salvo com sucesso!');
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar contato');
    }
  };

  const handleSend = () => {
    if (!isChatAllowed) return;
    if (!message.trim()) return;
    onSendMessage(message);
    setMessage('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    // Quick reply shortcut - show when typing /
    if (e.key === '/' && message === '') {
      e.preventDefault();
      setShowQuickReplies(true);
    }
    // Close quick replies on Escape
    if (e.key === 'Escape' && showQuickReplies) {
      setShowQuickReplies(false);
    }
  };

  const handleQuickReply = (qr) => {
    // Insert content into message field instead of sending directly
    // This allows the user to edit before sending
    setMessage(qr.content);
    setShowQuickReplies(false);
  };

  const handleMessageChange = (e) => {
    const value = e.target.value;
    setMessage(value);

    // Show quick replies when message starts with /
    if (value.startsWith('/')) {
      setShowQuickReplies(true);
    } else if (showQuickReplies && !value.startsWith('/')) {
      setShowQuickReplies(false);
    }
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try OGG Opus first (WhatsApp compatible), fallback to webm
      let mimeType = 'audio/ogg;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
        }
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Use .ogg extension for WhatsApp compatibility
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/ogg' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Erro ao acessar microfone. Verifique as permissões.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendAudio = async () => {
    if (!audioBlob) return;

    setUploading(true);
    try {
      // Convert blob to file with .ogg extension (WhatsApp compatible)
      const audioFile = new File([audioBlob], `audio_${Date.now()}.ogg`, { type: 'audio/ogg' });
      const response = await uploadFile(audioFile);
      const { file_url } = response.data;

      onSendMessage('Áudio', 'audio', `${BACKEND_URL}${file_url}`);
      toast.success('Áudio enviado!');
      setAudioBlob(null);
      setRecordingTime(0);
    } catch (error) {
      console.error('Error sending audio:', error);
      toast.error('Erro ao enviar áudio');
    } finally {
      setUploading(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo: 50MB');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadFile(file);
      const { file_url, file_name, file_type } = response.data;

      // Send message with file
      onSendMessage(file_name, file_type, `${BACKEND_URL}${file_url}`);
      toast.success('Arquivo enviado com sucesso!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Erro ao enviar arquivo');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleTransfer = (attendant) => {
    onTransfer(attendant);
    setShowTransferDialog(false);
  };

  // Filter quick replies based on search term (text after /)
  const searchTerm = message.startsWith('/') ? message.slice(1).toLowerCase() : '';
  const filteredQuickReplies = quickReplies.filter(qr => {
    if (!searchTerm) return true;
    return qr.shortcut.toLowerCase().includes(searchTerm) ||
      qr.title?.toLowerCase().includes(searchTerm) ||
      qr.content.toLowerCase().includes(searchTerm);
  });

  const availableAttendants = attendants.filter(a =>
    a.id !== currentUser?.id &&
    a.id !== conversation?.assigned_to
  );

  const getMessageStatusIcon = (status) => {
    const s = (status || '').toString();
    const up = s.toUpperCase();
    const normalized =
      up === 'DELIVERY_ACK' || up === 'DELIVERED' ? 'delivered'
        : up === 'READ' || up === 'READ_ACK' ? 'read'
          : up === 'SENT' ? 'sent'
            : s;

    switch (normalized) {
      case 'sent':
        return <Check size={14} className="text-muted-foreground" />;
      case 'delivered':
        return <CheckCheck size={14} className="text-muted-foreground" />;
      case 'read':
        return <CheckCheck size={14} className="text-primary" />;
      default:
        return null;
    }
  };

  // Render media content based on type
  const renderMediaContent = (msg) => {
    const mediaUrl = msg.media_url;
    // Some providers store structured content (contact/location) while keeping content_type as "text".
    // Normalize it here so the UI still renders as actionable cards.
    let contentType = msg.content_type;
    if (contentType === 'text' && msg.contact) contentType = 'contact';
    if (contentType === 'text' && msg.location) contentType = 'location';

    if (!mediaUrl && contentType === 'text') {
      return <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>;
    }

    // Build full URL if it's a relative path
    const fullUrl = mediaUrl?.startsWith('http') ? mediaUrl : `${BACKEND_URL}${mediaUrl}`;

    switch (contentType) {
      case 'contact': {
        const c = msg.contact || {};
        const parseVCard = (vcard) => {
          if (!vcard || typeof vcard !== 'string') return { name: null, phone: null };
          const fnMatch = vcard.match(/^\s*FN\s*:(.+)\s*$/im);
          const telMatch = vcard.match(/^\s*TEL[^:]*:([+\d][\d\s().-]+)\s*$/im);
          return {
            name: fnMatch?.[1]?.trim() || null,
            phone: telMatch?.[1]?.trim() || null,
          };
        };

        const vcardParsed = parseVCard(msg.content);
        const phonesArray = Array.isArray(c.phones) ? c.phones : [];
        const phoneFromArray =
          phonesArray[0]?.phone ||
          phonesArray[0]?.number ||
          phonesArray[0]?.wa_id ||
          phonesArray[0]?.waId ||
          null;

        const name =
          c.name ||
          c.full_name ||
          c.fullName ||
          vcardParsed.name ||
          (typeof msg.content === 'string' && msg.content.length < 80 ? msg.content : null) ||
          'Contato';

        const phone =
          c.phone ||
          c.number ||
          c.phone_number ||
          c.phoneNumber ||
          phoneFromArray ||
          vcardParsed.phone ||
          '';
        return (
          <button
            type="button"
            onClick={() => onOpenContact && onOpenContact({ name, phone })}
            className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors border border-slate-200"
          >
            <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
              <UserPlus size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{name}</p>
              {phone && <p className="text-xs text-muted-foreground truncate">{phone}</p>}
              <p className="text-xs text-muted-foreground mt-1">Clique para abrir conversa</p>
            </div>
          </button>
        );
      }
      case 'location': {
        const loc = msg.location || {};
        const lat = loc.latitude ?? loc.lat ?? null;
        const lon = loc.longitude ?? loc.lng ?? loc.long ?? null;
        const hasCoords = lat !== null && lon !== null && lat !== undefined && lon !== undefined;
        const mapsUrl = hasCoords ? `https://www.google.com/maps?q=${encodeURIComponent(`${lat},${lon}`)}` : null;
        const latNum = hasCoords ? Number(lat) : null;
        const lonNum = hasCoords ? Number(lon) : null;
        const canEmbed = Number.isFinite(latNum) && Number.isFinite(lonNum);
        const delta = 0.01;
        const bbox = canEmbed
          ? `${lonNum - delta},${latNum - delta},${lonNum + delta},${latNum + delta}`
          : null;
        const osmEmbedUrl = canEmbed
          ? `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${encodeURIComponent(`${latNum},${lonNum}`)}`
          : null;
        return (
          <div className="space-y-2">
            {mapsUrl ? (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                📍 Abrir localização no mapa
              </a>
            ) : (
              <p className="text-sm whitespace-pre-wrap break-words">{msg.content || '📍 Localização'}</p>
            )}
            {osmEmbedUrl && (
              <div className="w-full h-36 rounded-lg overflow-hidden border border-border/50">
                <iframe
                  title="Mapa"
                  src={osmEmbedUrl}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
            {hasCoords && (
              <p className="text-xs text-muted-foreground">
                {lat}, {lon}
              </p>
            )}
          </div>
        );
      }
      case 'image':
        return (
          <div className="space-y-2">
            <a href={fullUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={fullUrl}
                alt={msg.content || 'Imagem'}
                className="max-w-full max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center gap-2 p-3 bg-slate-100 rounded-lg">
                <Image size={20} className="text-muted-foreground" />
                <span className="text-sm">{msg.content || 'Imagem'}</span>
              </div>
            </a>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-2">
            <video
              src={fullUrl}
              controls
              className="max-w-full max-h-64 rounded-lg"
            >
              Seu navegador não suporta vídeos.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="audio-message">
            <div className="flex items-center gap-3 min-w-[280px]">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Music size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium mb-1 opacity-80">Mensagem de áudio</p>
                <audio src={fullUrl} controls className="w-full h-8 audio-player">
                  Seu navegador não suporta áudio.
                </audio>
              </div>
            </div>
          </div>
        );

      case 'document':
        return (
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 min-w-[250px] hover:opacity-90 transition-opacity"
          >
            <div className="w-12 h-12 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
              <FileText size={24} className="text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{msg.content || 'Documento'}</p>
              <p className="text-xs opacity-60 mt-0.5">Clique para baixar</p>
            </div>
            <Download size={18} className="opacity-60 flex-shrink-0" />
          </a>
        );

      default:
        // For text or unknown types
        if (mediaUrl) {
          return (
            <div className="space-y-2">
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <Paperclip size={14} />
                <span className="text-sm">{msg.content || 'Arquivo'}</span>
              </a>
            </div>
          );
        }
        return <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>;
    }
  };

  if (!conversation) {
    return (
      <div className="chat-window" data-testid="chat-window-empty">
        <div className="empty-state">
          <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mb-5">
            <Phone className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-600 mb-1">Selecione uma conversa</h3>
          <p className="text-sm text-slate-400">
            Escolha uma conversa na lista para começar o atendimento
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-window" data-testid="chat-window">
      {/* Header */}
      <div className="chat-header">
        <div className="flex items-center gap-2">
          {isMobile && (
            <Button variant="ghost" size="icon" onClick={onBack} className="mr-1 h-8 w-8 shrink-0">
              <ArrowLeft size={20} />
            </Button>
          )}
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="w-10 h-10 shrink-0">
              {conversation.customer_avatar_url && (
                <AvatarImage
                  src={conversation.customer_avatar_url.startsWith('http') ? conversation.customer_avatar_url : `${BACKEND_URL}${conversation.customer_avatar_url}`}
                  alt={conversation.customer_name || conversation.customer_phone}
                />
              )}
              <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                {getInitials(conversation.customer_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 overflow-hidden">
              <h3 className="font-semibold text-slate-800 truncate">{conversation.customer_name || conversation.customer_phone}</h3>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={cn('text-xs px-1.5 py-0 shrink-0', getStatusColor(conversation.status))}>
                  {getStatusLabel(conversation.status)}
                </Badge>
                <span className="text-xs text-muted-foreground truncate">{conversation.customer_phone}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="chat-menu-btn">
                <MoreVertical size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {/* Assign/Unassign options */}
              {!conversation.assigned_to ? (
                <DropdownMenuItem
                  onClick={onAssign}
                  data-testid="menu-assign"
                >
                  <UserPlus className="mr-2" size={16} />
                  Atribuir a mim
                </DropdownMenuItem>
              ) : conversation.assigned_to === currentUser?.id ? (
                <DropdownMenuItem
                  onClick={onUnassign}
                  data-testid="menu-unassign"
                >
                  <X className="mr-2" size={16} />
                  Remover atribuição
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                onClick={() => setShowTransferDialog(true)}
                data-testid="menu-transfer"
              >
                <UserPlus className="mr-2" size={16} />
                Transferir
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {!contactExists && (
                <DropdownMenuItem
                  onClick={handleOpenSaveContact}
                  disabled={contactLoading}
                >
                  <UserPlus className="mr-2" size={16} />
                  Salvar como contato
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {conversation.status === 'closed' ? (
                <DropdownMenuItem onClick={onReopen} data-testid="menu-reopen">
                  <RotateCcw className="mr-2" size={16} />
                  Reabrir conversa
                </DropdownMenuItem>
              ) : conversation.status === 'closing' ? (
                <DropdownMenuItem disabled data-testid="menu-closing">
                  <XCircle className="mr-2" size={16} />
                  Encerrando...
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={onClose} className="text-destructive" data-testid="menu-close">
                  <XCircle className="mr-2" size={16} />
                  Encerrar conversa
                </DropdownMenuItem>
              )}
              {deleteEnabled && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setShowDeleteConfirm(true)}
                    className="text-destructive"
                    data-testid="menu-delete"
                  >
                    <Trash2 className="mr-2" size={16} />
                    Excluir conversa
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0 p-4">
        <div className="space-y-4">
          {messages.map((msg) => (
            <div
              key={msg.id}
              data-testid={`message-${msg.id}`}
              className={cn(
                'message',
                msg.sender_type === 'customer' ? 'justify-start' : 'justify-end'
              )}
            >
              {msg.sender_type === 'customer' && (
                <Avatar className="w-8 h-8 mt-1">
                  {conversation.customer_avatar_url && (
                    <AvatarImage
                      src={conversation.customer_avatar_url.startsWith('http') ? conversation.customer_avatar_url : `${BACKEND_URL}${conversation.customer_avatar_url}`}
                      alt={conversation.customer_name || conversation.customer_phone}
                    />
                  )}
                  <AvatarFallback className="bg-slate-200 text-slate-600 text-xs font-medium">
                    {getInitials(msg.sender_name)}
                  </AvatarFallback>
                </Avatar>
              )}

              <div
                className={cn('message-content', (() => {
                  const isMedia = ['audio', 'document', 'image', 'video'].includes(msg.content_type);
                  if (isMedia) return null;
                  if (msg.sender_type === 'customer') return 'chat-bubble-customer';
                  if (msg.sender_type === 'attendant') return 'chat-bubble-attendant';
                  if (msg.sender_type === 'bot') return 'chat-bubble-bot';
                  if (msg.sender_type === 'ai') return 'chat-bubble-ai';
                  if (msg.sender_type === 'system') return 'chat-bubble-system';
                  return null;
                })())}
              >
                {msg.sender_type !== 'customer' && (
                  <p className="text-xs opacity-70 mb-1">
                    {msg.sender_type === 'bot' ? '🤖 Bot' :
                      msg.sender_type === 'ai' ? '🤖 IA' :
                        msg.sender_type === 'system' ? '⚙️ Sistema' :
                          msg.sender_name || 'Atendente'}
                  </p>
                )}
                {renderMediaContent(msg)}
                <div className="flex items-center justify-end gap-1 mt-1">
                  <span className="text-xs opacity-60">{formatTime(msg.timestamp)}</span>
                  {msg.sender_type !== 'customer' && getMessageStatusIcon(msg.status)}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Quick Replies Dropdown */}
      {showQuickReplies && (
        <div className="absolute bottom-20 left-4 right-4 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-auto z-10">
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Zap size={14} className="text-primary" />
              <span className="font-medium">Respostas Rápidas</span>
              {searchTerm && <span className="text-xs text-slate-400">— Buscando: {searchTerm}</span>}
            </div>
          </div>
          {filteredQuickReplies.length > 0 ? (
            filteredQuickReplies.map((qr) => (
              <button
                key={qr.id}
                onClick={() => handleQuickReply(qr)}
                className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <code className="text-xs text-primary bg-primary/10 px-1.5 py-0.5 rounded-md font-mono">/{qr.shortcut}</code>
                  {qr.title && <span className="text-sm font-medium">{qr.title}</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{qr.content}</p>
              </button>
            ))
          ) : (
            <div className="px-4 py-6 text-center text-muted-foreground">
              <p className="text-sm">Nenhuma resposta rápida encontrada</p>
              <p className="text-xs mt-1">Configure respostas rápidas em Configurações</p>
            </div>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
      />

      {/* Input Area */}
      <div className="chat-input-area">
        {/* Warning if not assigned (or not assigned to me) */}
        {!isAssignedToMe && !isClosed && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <UserPlus className="text-amber-600 shrink-0" size={18} />
            <span className="text-sm text-amber-700">
              {!conversation.assigned_to
                ? 'Atribua esta conversa a você para enviar mensagens'
                : `Conversa atribuída a ${conversation.assigned_name || 'outro atendente'}.`}
            </span>
            {!conversation.assigned_to && (
              <Button
                size="sm"
                variant="outline"
                className="ml-auto border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={onAssign}
              >
                Atribuir a mim
              </Button>
            )}
          </div>
        )}

        {/* Recording Mode */}
        {(isRecording || audioBlob) ? (
          <div className="flex items-center gap-3">
            {isRecording ? (
              <>
                <div className="flex items-center gap-2 flex-1 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-red-600 font-mono">{formatRecordingTime(recordingTime)}</span>
                  <span className="text-sm text-slate-500 ml-2">Gravando...</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={cancelRecording}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={20} />
                </Button>
                <Button
                  onClick={stopRecording}
                  className="bg-red-500 hover:bg-red-600"
                >
                  <Square size={18} />
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 flex-1 bg-slate-100 border border-slate-200 rounded-lg px-4 py-2">
                  <Music size={18} className="text-primary" />
                  <span className="text-sm">Áudio gravado</span>
                  <span className="text-xs text-muted-foreground">({formatRecordingTime(recordingTime)})</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={cancelRecording}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 size={20} />
                </Button>
                <Button
                  onClick={sendAudio}
                  disabled={uploading}
                  className="bg-primary hover:bg-primary/90"
                >
                  {uploading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </Button>
              </>
            )}
          </div>
        ) : (
          /* Normal Mode */
          <div className="flex items-end gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground mb-1"
              onClick={() => setShowQuickReplies(!showQuickReplies)}
              disabled={!isChatAllowed}
              data-testid="quick-reply-btn"
            >
              <Zap size={20} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-foreground mb-1"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isChatAllowed || uploading}
              data-testid="attach-btn"
            >
              {uploading ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Paperclip size={20} />
              )}
            </Button>
            <Textarea
              ref={textareaRef}
              data-testid="message-input"
              placeholder={
                chatDisabled
                  ? chatDisabledReason
                  : (!isAssignedToMe
                    ? "Conversa não atribuída a você."
                    : "Digite / para respostas rápidas...")
              }
              className="flex-1 bg-slate-50 border border-slate-200 focus-visible:ring-primary/30 min-h-[40px] max-h-[120px] resize-none py-2 text-sm leading-relaxed"
              rows={1}
              value={message}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              disabled={!isChatAllowed}
            />
            {message.trim() ? (
              <Button
                onClick={handleSend}
                disabled={!isChatAllowed}
                className="bg-primary hover:bg-primary/90 mb-1"
                data-testid="send-btn"
              >
                <Send size={18} />
              </Button>
            ) : (
              <Button
                onClick={startRecording}
                disabled={!isChatAllowed}
                className="bg-slate-700 hover:bg-slate-800 mb-1"
                data-testid="record-btn"
              >
                <Mic size={18} />
              </Button>
            )}
          </div>
        )}
        {chatDisabled && (
          <p className="text-xs text-destructive text-center mt-2">
            Chat desabilitado: {chatDisabledReason}
          </p>
        )}
        {!chatDisabled && conversation.status === 'closing' && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            Encerrando conversa... aguarde um momento.
          </p>
        )}
        {!chatDisabled && conversation.status === 'closed' && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            Esta conversa foi encerrada. Reabra para continuar.
          </p>
        )}
      </div>

      {/* Save Contact Dialog */}
      <Dialog open={showSaveContactDialog} onOpenChange={setShowSaveContactDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono">Salvar como Contato</DialogTitle>
            <DialogDescription>
              Confirme ou edite os dados do contato antes de salvar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Nome</label>
              <Input
                value={contactFormData.name}
                onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                placeholder="Nome do contato"
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Telefone</label>
              <Input
                value={contactFormData.phone}
                readOnly
                className="bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed"
              />
            </div>
            <div className="grid gap-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                value={contactFormData.email}
                onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                placeholder="email@exemplo.com"
                className="bg-slate-50 border-slate-200"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label className="text-sm font-medium">Plano</label>
                <Input
                  value={contactFormData.plan}
                  onChange={(e) => setContactFormData({ ...contactFormData, plan: e.target.value })}
                  placeholder="Plano"
                  className="bg-slate-50 border-slate-200"
                />
              </div>
              <div className="grid gap-2">
                <label className="text-sm font-medium">Contrato</label>
                <Input
                  value={contactFormData.contract}
                  onChange={(e) => setContactFormData({ ...contactFormData, contract: e.target.value })}
                  placeholder="Contrato"
                  className="bg-slate-50 border-slate-200"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveContactDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveContact}>
              Salvar Contato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono">Transferir Atendimento</DialogTitle>
            <DialogDescription>
              Selecione um atendente para transferir esta conversa
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-4">
            {availableAttendants.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhum atendente disponível para transferência
              </p>
            ) : (
              availableAttendants.map((attendant) => (
                <button
                  key={attendant.id}
                  onClick={() => handleTransfer(attendant)}
                  className="w-full p-3 flex items-center gap-3 rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`transfer-to-${attendant.id}`}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarFallback className={cn(
                      "text-sm font-semibold",
                      attendant.status === 'online' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    )}>
                      {getInitials(attendant.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{attendant.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {attendant.role} • {attendant.status === 'online' ? 'Online' : 'Ocupado'}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm && deleteEnabled} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-mono text-destructive flex items-center gap-2">
              <Trash2 size={20} />
              Excluir Conversa
            </DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir esta conversa? Esta ação não pode ser desfeita.
              Todas as mensagens serão permanentemente removidas.
            </DialogDescription>
          </DialogHeader>

          <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-sm font-medium">{conversation?.customer_name || 'Cliente'}</p>
            <p className="text-xs text-muted-foreground">{conversation?.customer_phone}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {messages?.length || 0} mensagens serão excluídas
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                onDelete?.();
                setShowDeleteConfirm(false);
              }}
              data-testid="confirm-delete"
            >
              <Trash2 className="mr-2" size={16} />
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChatWindow;
